import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";

function StatCard({ label, value, sub }) {
  return (
    <div className="glass rounded-2xl p-6">
      <div className="text-[11px] tracking-[0.22em] uppercase text-white/40 mb-2">{label}</div>
      <div className="font-display text-4xl text-white">{value ?? "—"}</div>
      {sub && <div className="text-sm text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    const token = getToken();
    fetch("/api/admin/metrics", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { setMetrics(data); setLoading(false); })
      .catch((e) => { setError("Failed to load metrics: " + e); setLoading(false); });
  }, []);

  return (
    <AdminShell>
      <div className="p-6 md:p-10 text-white">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Admin</div>
        <h1 className="font-display text-3xl mb-8">Metrics</h1>

        {loading && <div className="text-white/40">Loading…</div>}
        {error   && <div className="text-red-400">{error}</div>}

        {metrics && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <StatCard label="Total Users"         value={metrics.total_users}          />
              <StatCard label="Active Users"        value={metrics.active_users}         />
              <StatCard label="Onboarding Complete" value={metrics.onboarding_completed} />
              <StatCard label="New (7 days)"        value={metrics.recent_signups_7d}    />
              <StatCard label="New (24h)"           value={metrics.new_users_24h ?? 0}   />
              <StatCard label="H2H Rounds"          value={metrics.match_activity?.head_to_head_rounds ?? 0} sub="matches played" />
              <StatCard label="Finalists Saved"     value={metrics.match_activity?.finalists_saved ?? 0}     sub="shortlist locked" />
            </div>

            {metrics.users_by_state?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Users by State</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {metrics.users_by_state.map((row) => (
                    <div key={row.state} className="glass rounded-xl px-4 py-3 flex items-center justify-between">
                      <span className="text-white/70 font-medium">{row.state}</span>
                      <span className="text-[#F2DE94] font-semibold">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {metrics.gender_breakdown?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Gender Breakdown</h2>
                <div className="flex gap-4">
                  {metrics.gender_breakdown.map((row) => {
                    const total = metrics.gender_breakdown.reduce((s, r) => s + r.count, 0);
                    const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
                    return (
                      <div key={row.gender} className="glass rounded-2xl p-5 flex-1 text-center">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-1 capitalize">{row.gender}</div>
                        <div className="font-display text-3xl text-[#F2DE94]">{row.count}</div>
                        <div className="text-white/40 text-sm mt-1">{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminShell>
  );
}
